import type React from "react";

import { useTranslation } from "react-i18next";

import Color from "color";

import { cx, styled } from "simplydo/react/styled";
import { mobileOnly, tabletUp, useTheme } from "simplydo/react/theme";
import { Container, Divider, Grid, Segment } from "simplydo/react/ui";

import { useAppSelector } from "store";

export const StyledDashboardContent = styled.div`
  flex: 1;
  position: relative;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
`;

const TransitionSegment = styled(Segment)`
  opacity: 0;
  animation-duration: 0.7s;
  animation-fill-mode: forwards;
  @keyframes slideInFull {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: 0;
      opacity: 1;
    }
  }
  @keyframes slideInOffset {
    0% {
      margin-top: -20%;
      opacity: 0;
    }
    100% {
      margin-top: -5%;
      opacity: 1;
    }
  }
  &.slide-full {
    animation-name: slideInFull;
  }
  &.slide-offset {
    animation-name: slideInOffset;
  }
`;

const MaxContent = styled.div`
  position: relative;
  min-height: 40px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 2px;

  &.is-scrolling {
    max-height: 70vh;
  }

  ${mobileOnly} {
    padding-right: 0px;
  }

  ${tabletUp} {
    padding-right: 10px;
  }
`;

const ResponsiveColumn = styled(Grid.Column)`
  &.is-mobile {
    width: 100% !important;
    min-width: auto !important;
  }
  &.is-fluid {
    width: 62.5% !important;
    min-width: 600px !important;
  }
  &.is-narrow {
    width: 37.5% !important;
    min-width: 600px !important;
  }
`;

// Better visual gradient, source https://css-tricks.com/easing-linear-gradients/
const scrimGradient = (colorStr: string) => {
  const color = Color(colorStr);

  return `radial-gradient(
    80vw 80vh at 100% 100%,
    ${color.rgb()} 0%,
    ${color.alpha(0.738).rgb()} 19%,
    ${color.alpha(0.541).rgb()} 34%,
    ${color.alpha(0.382).rgb()} 47%,
    ${color.alpha(0.278).rgb()} 56.5%,
    ${color.alpha(0.194).rgb()} 65%,
    ${color.alpha(0.126).rgb()} 73%,
    ${color.alpha(0.075).rgb()} 80.2%,
    ${color.alpha(0.042).rgb()} 86.1%,
    ${color.alpha(0.021).rgb()} 91%,
    ${color.alpha(0.008).rgb()} 95.2%,
    ${color.alpha(0.002).rgb()} 98.2%,
    transparent 100%
  )`;
};

type StyledDashboardProps = {
  content: React.ReactNode;
  hideName?: boolean;
  fluid?: boolean;
  excludeWelcomeText?: boolean;
  scrolling?: boolean;
  padded?: boolean | "very";
  backgroundColor?: string;
};

const AppStyledDashboard = ({
  content,
  hideName,
  fluid,
  excludeWelcomeText,
  scrolling = true,
  padded = true,
  backgroundColor,
}: StyledDashboardProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const urlOrganisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const organisation = user?.ownerOrganisation || urlOrganisation;
  const theme = useTheme();

  return (
    <StyledDashboardContent
      style={{
        position: "relative",
        paddingTop: scrolling ? 0 : 80,
        paddingBottom: scrolling ? 0 : 80,
      }}
    >
      <div
        style={{
          transition: "opacity 0.3s ease",
          opacity: organisation ? 1 : 0,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: scrimGradient(organisation?.primaryColour || "#fff"),
        }}
      />
      {backgroundColor ? (
        <div
          style={{
            transition: "opacity 0.3s ease",
            opacity: organisation ? 0 : 1,
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: scrimGradient(backgroundColor),
          }}
        />
      ) : null}
      <Container fluid style={{ justifyContent: "center" }}>
        <Grid stackable centered>
          <ResponsiveColumn
            className={cx(
              theme.sizes.isMobile
                ? "is-mobile"
                : fluid
                  ? "is-fluid"
                  : "is-narrow",
            )}
          >
            <TransitionSegment
              className={cx(
                theme.sizes.isMobile || fluid ? "slide-full" : "slide-offset",
              )}
              raised
              padded={padded}
              fluid={fluid}
            >
              {!excludeWelcomeText ? (
                <>
                  <h1>
                    <span
                      role="img"
                      aria-label="Wave"
                      style={{ marginRight: 12 }}
                    >
                      👋
                    </span>
                    {!hideName && user?.profile?.firstName
                      ? t("entry.enrol.welcome_name", {
                          firstName: user?.profile?.firstName,
                        })
                      : t("entry.enrol.welcome")}
                  </h1>
                  <Divider />
                </>
              ) : null}
              <MaxContent className={cx(scrolling && "is-scrolling")}>
                {content}
              </MaxContent>
            </TransitionSegment>
          </ResponsiveColumn>
        </Grid>
      </Container>
    </StyledDashboardContent>
  );
};

export default AppStyledDashboard;
